import { memo, useState } from "react";
import { Col, Row } from "reactstrap";
import LI from "../ListGroup/ListItem";
import UL from "../ListGroup/UnorderedList";
import P from "../Paragraph";
import IconComponent from "../IconComponent";
import styled, { keyframes, css } from "styled-components";
import { H5 } from "../../AbstractElements";
import { EXCEL, Images, PDF } from "../../utils/Constant";
import ProductImageCard from "../ProductImageCard";

/* ---------- Props ---------- */
export interface IDetailsInfoProps<T extends object> {
  detailsInfo: T;
  exclusionKeys?: Array<string>;
  handleDownload?: (fileUrl: string) => void;
}

/* ---------- File Type Groups ---------- */
type ExtensionTypes = {
  img: string[];
  pdf: string[];
  excel: string[];
};

const extensionsMap: Record<string, keyof ExtensionTypes> = {
  jpeg: "img",
  jpg: "img",
  png: "img",
  webp: "img",
  gif: "img",
  svg: "img",
  pdf: "pdf",
  xls: "excel",
  xlsx: "excel",
  xlsm: "excel",
  xlsb: "excel",
  csv: "excel",
};

/* ---------- Animations ---------- */
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeOut = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(10px); }
`;

const AnimatedWrapper = styled.div<{ $isVisible: boolean }>`
  animation: ${({ $isVisible }) =>
    $isVisible
      ? css`
          ${fadeIn} 0.3s ease forwards
        `
      : css`
          ${fadeOut} 0.3s ease forwards
        `};
`;

const AttachmentWrapper = styled.div`
  text-align: center;
  padding: 1rem;
  animation: ${fadeIn} 0.3s ease forwards;
`;

const BackButton = styled.button`
  border: none;
  background: none;
  color: #0d6efd;
  font-weight: 500;
  margin-bottom: 1rem;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

/* ---------- Helpers ---------- */
const categorizeFiles = (files: string[]): ExtensionTypes => {
  const categorized = { img: [], pdf: [], excel: [] } as ExtensionTypes;
  files.forEach((file) => {
    const ext = file.split(".").pop()?.toLowerCase() || "";
    const cat = extensionsMap[ext];
    if (cat) categorized[cat].push(file);
  });
  return categorized;
};

/* ---------- Component ---------- */
const DetailsInfo = <T extends object>({
  detailsInfo,
  exclusionKeys = [],
  handleDownload,
}: IDetailsInfoProps<T>) => {
  const [showAttachment, setShowAttachment] = useState(false);
  const [collection, setCollection] = useState<ExtensionTypes>({
    img: [],
    pdf: [],
    excel: [],
  });

  const handleOpenAttachment = (attachmentValue: unknown) => {
    if (!attachmentValue) return;

    const files =
      typeof attachmentValue === "string"
        ? attachmentValue
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : Array.isArray(attachmentValue)
        ? attachmentValue.filter((x): x is string => typeof x === "string")
        : [];

    if (files.length) {
      const categorized = categorizeFiles(files);
      setCollection(categorized);
      setShowAttachment(true);
    }
  };

  return (
    <>
      {!showAttachment ? (
        <AnimatedWrapper $isVisible={!showAttachment}>
          <UL>
            {Object.entries(detailsInfo)
              .filter(([key]) => !exclusionKeys.includes(key))
              .map(([key, value]) => (
                <LI key={key}>
                  <Row>
                    <Col md="4">
                      <P className="text-capitalize m-0">{key}</P>
                    </Col>
                    <Col md="1">
                      <P className="m-0 p-0">-</P>
                    </Col>
                    <Col md="7">
                      {key === "attachment" && value ? (
                        <IconComponent
                          className="text-primary f-18 fa fa-eye pointer"
                          onClick={() => handleOpenAttachment(value)}
                        />
                      ) : (
                        <P className="text-capitalize m-0">
                          {value !== null ? value : ""}
                        </P>
                      )}
                    </Col>
                  </Row>
                </LI>
              ))}
          </UL>
        </AnimatedWrapper>
      ) : (
        <AttachmentWrapper>
          <BackButton onClick={() => setShowAttachment(false)}>
            <IconComponent className="fa fa-long-arrow-left text-primary f-18 me-1" />{" "}
            Back to details
          </BackButton>

          {/* IMAGES */}
          {collection?.img?.length > 0 && (
            <Row className="mb-5">
              <H5 className="text-underline mb-3">{Images}</H5>
              {collection.img.map((src, index) => (
                <Col
                  key={index}
                  xs="12"
                  md={collection.img.length === 1 ? "12" : "6"}
                  className="mb-3"
                >
                  <ProductImageCard
                    key={index}
                    imageSrc={src}
                    altImage="attachment"
                    handleDownload={handleDownload}
                    isShowHover={true}
                  />
                </Col>
              ))}
            </Row>
          )}

          {/* PDF */}
          {collection?.pdf?.length > 0 && (
            <Row className="mb-5">
              <H5 className="text-underline mb-3">{PDF}</H5>
              {collection.pdf.map((file, index) => (
                <Col xs="12" key={index} className="mb-3">
                  <iframe
                    className="w-100"
                    src={file}
                    style={{ height: "500px", borderRadius: "8px" }}
                    title={`pdf-${index}`}
                  />
                </Col>
              ))}
            </Row>
          )}

          {/* EXCEL */}
          {collection?.excel?.length > 0 && (
            <Row>
              <H5 className="text-underline mb-3">{EXCEL}</H5>
              <UL>
                {collection.excel.map((file, index) => (
                  <LI key={index}>
                    <P className="m-0">
                      <a href={file} target="_blank" download>
                        {file}
                      </a>
                    </P>
                  </LI>
                ))}
              </UL>
            </Row>
          )}
        </AttachmentWrapper>
      )}
    </>
  );
};

export default memo(DetailsInfo);
